import classNames from 'classnames';
import React from 'react';
import Icon from '@/components/atoms/icon/icon';
import Typography from '@/components/atoms/typography/typography';
import { useSearchContext } from '@/libraries/contexts/search-context';
import styles from './search-bar-area-selector-button.module.scss';

export interface SearchBarAreaSelectorButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** The available areas to choose from */
  areas: Content.Components['TravelPortalAvailableArea'][];
  /** The class name to apply */
  className?: string;
  /** Indicates whether the field is disabled */
  disabled?: boolean;
  /** Indicates whether the field is active */
  isActive?: boolean;
  /** Indicates whether the field is notified that has changes */
  isNotified?: boolean;
  /** An event that is triggered on click */
  onClick?: (event?: React.MouseEvent<HTMLButtonElement>) => void;
}

const SearchBarAreaSelectorButton = ({
  areas,
  className,
  disabled = false,
  isActive = false,
  isNotified = false,
  ...rest
}: SearchBarAreaSelectorButtonProps) => {
  const { area } = useSearchContext();

  const areaMap = React.useMemo(
    () =>
      areas.reduce<Record<string, { headline: string; icon: IconName }>>(
        (acc, item) => {
          acc[item.id] = {
            headline: item.label,
            icon: item.icon,
          };

          return acc;
        },
        {},
      ),
    [areas],
  );

  const selectedArea =
    (area ? areaMap[area] : null) ??
    (areas.length > 0
      ? {
          headline: areas[0].label,
          icon: areas[0].icon,
        }
      : null);

  return (
    <button
      {...rest}
      className={classNames(styles.component, className, {
        [styles.isActive]: isActive,
        [styles.isDisabled]: disabled,
        [styles.isNotified]: isNotified,
      })}
      tabIndex={0}
    >
      <div className={styles.areaContent}>
        <div className={styles.areaHeader}>
          <Icon name={selectedArea?.icon ?? 'Explore'} size={24} />

          <Typography tagStyle="bodySmallBold">
            {selectedArea?.headline ?? ''}
          </Typography>
        </div>

        <Icon
          aria-hidden="true"
          className={styles.areaChevron}
          name="ChevronDown"
          size={14}
        />
      </div>
    </button>
  );
};

SearchBarAreaSelectorButton.displayName = 'SearchBarAreaSelectorButton';

export default SearchBarAreaSelectorButton;
